// src/pages/UserAuthCallbackPage.js (User App)
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom'; // Added Link
import { useParticipantAuth } from '../context/ParticipantAuthContext';
import Modal from '../components/Modal';

const UserAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authContextLoading } = useParticipantAuth();

  const [message, setMessage] = useState('Processing authentication...');
  const [isError, setIsError] = useState(false);
  const [processedToken, setProcessedToken] = useState(false); // Track if token processing has been attempted

  // Extract program details from URL for potential error redirects
  const programId = searchParams.get('program_id');
  const programName = searchParams.get('name');
  const programShortName = searchParams.get('p');

  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (authContextLoading) {
        setMessage("Initializing authentication...");
        return; // Wait for auth context to finish its initial loading
    }

    if (token && !processedToken) {
      setMessage('Authentication successful! Finalizing login...');
      setIsError(false);
      login(token); // This will update isAuthenticated in the context
      setProcessedToken(true); // Mark token as processed
    } else if (error && !processedToken) {
      const userFriendlyError = error.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      setMessage(`Authentication failed: ${userFriendlyError}. Please try again.`);
      setIsError(true);
      setProcessedToken(true);
    } else if (!token && !error && !isAuthenticated && !processedToken && !authContextLoading) {
      // This case means we landed here without a token or error, and we're not already authed.
      setMessage('Invalid authentication callback. Missing token or error information.');
      setIsError(true);
      setProcessedToken(true);
    }
  }, [searchParams, login, isAuthenticated, authContextLoading, processedToken]);

  useEffect(() => {
    // This effect runs when isAuthenticated (from context) becomes true
    // AND we have successfully processed a token (indicated by !isError and processedToken).
    if (isAuthenticated && processedToken && !isError) {
      setMessage('Login complete! Redirecting to your profile...');
      const timer = setTimeout(() => {
        navigate('/profile', { replace: true });
      }, 1000); // Delay to show message
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, processedToken, isError, navigate]);

  const handleGoBackToLogin = () => {
    if (programId && programName) {
      navigate(`/login?program_id=${programId}&name=${encodeURIComponent(programName)}&p=${programShortName || ''}`);
    } else if (programShortName) {
      // If only short name is available, go to program landing, user can click login again
      navigate(`/?p=${programShortName}`);
    } else {
      // Fallback to generic home if no program context at all
      navigate('/');
    }
  };

  // Determine Modal Title and Content
  let modalTitle = "Authentication";
  let modalContent = <p>{message}</p>;

  if (isError) {
    modalTitle = "Authentication Problem";
    modalContent = (
      <>
        <div className="message-container error-message" style={{ textAlign: 'center' }}>
          <p>{message}</p>
        </div>
        <button 
          onClick={handleGoBackToLogin} 
          style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
        >
          Try Login Again
        </button>
      </>
    );
  } else if (processedToken && isAuthenticated) {
    modalTitle = "Login Successful";
    modalContent = (
      <div className="message-container info-message" style={{ textAlign: 'center' }}>
        <p>{message}</p>
      </div>
    );
  } else if (processedToken && !isError) {
    // Token processed, waiting for isAuthenticated to become true
    modalTitle = "Finalizing Login";
     modalContent = (
      <div className="message-container info-message" style={{ textAlign: 'center' }}>
        <p>{message}</p>
        <p>Please wait...</p>
      </div>
    );
  } else if (authContextLoading) {
    modalTitle = "Loading";
    modalContent = (
        <div className="message-container info-message" style={{ textAlign: 'center' }}>
            <p>Initializing authentication...</p>
        </div>
    );
  }


  return (
    // Using a Modal for consistent UI, even if it's just for a brief message
    // The onClose for the modal could also be handleGoBackToLogin for errors.
    <Modal isOpen={true} onClose={isError ? handleGoBackToLogin : () => {}} title={modalTitle}>
      {modalContent}
      {!isError && !isAuthenticated && processedToken && (
        <div style={{marginTop: '20px', fontSize: '0.9em', textAlign: 'center'}}>
            <p>If you are not redirected automatically, please <Link to="/profile">click here</Link> or <button onClick={handleGoBackToLogin}>try logging in again</button>.</p>
        </div>
      )}
    </Modal>
  );
};

export default UserAuthCallbackPage;
